import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {useNavigate} from "react-router-dom";
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { withSwal } from 'react-sweetalert2';
import { toast } from 'react-toastify';
import MarkDownContent from '../../elements/MarkDownContent';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import { styled } from '@mui/material/styles';
import { Box, Button, Card, Grid } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import { isJsonString, uploadToS3 } from '../../utils/globalFunctions';
import ReadOnly from '../../elements/Editor/readOnly';
import SkillChips from '../../elements/SkillChip/SkillChipV3';
import { FlexRowWrapper } from '../../elements/Row';
import { SubTitle, Title } from '../../elements/Header';
import { DEFAULT_COMPANY_LIST_LOGO_URL } from '../../utils/constant';
import JobApplicationModal from '../Modals/jobApplicationModal';
import { createJobApplication } from '../../services/job.service';

import './styles.scss';
import '../../elements/Editor/styles.scss';

dayjs.extend(relativeTime)

const MainCard = styled(Card)`
  padding: 4px;
  display: flex;
  flex-direction: column;
  max-height: 800px;
  min-width: 500px;
  max-width: 1400px;
  height:100%;
  border-radius: 14px;
  box-shadow: ${({mainCardStyle = {}}) => mainCardStyle.boxShadow ? mainCardStyle.boxShadow : "rgba(67, 71, 85, 0.27) 0px 0px 0.25em, rgba(90, 125, 188, 0.05) 0px 0.25em 1em"};
  color: ${({mainCardStyle = {}}) => mainCardStyle.color ? mainCardStyle.color : "black"};
  position: ${({mainCardStyle = {}}) => mainCardStyle.position ? mainCardStyle.position : "relative"};
  border: 0;
  background-color: ${({mainCardStyle = {}}) => mainCardStyle.backgroundColor ? mainCardStyle.backgroundColor : "white"}   
  ${({hoverEffect}) => hoverEffect ? `&:hover {
      box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;
  }` : ""}
`

const LogoWrapper = styled("div")`
   width: 150px;
   height: 150px;
   background-size: contain;
   background-repeat: no-repeat;
   border-radius:10px;
   border: 1px solid #f0f0f0;
`;


const CustomButton = styled(Button)(() => ({
  color: '#283665',
  borderColor: '#283665',
  marginTop: '4px',
  '&:hover': {
    borderColor: '#283665',
  },
}));

const ResetButton = styled(Button)(() => ({
  marginTop: '4px'
}));

const StatusSpan = styled('span')(() => ({
  color: '#283665',
  fontWeight: 600,
  cursor: 'pointer'
}));

const colorStatus = {
  'pending':"#6c757d",
  'approved':"#28a745",
  'rejected':"#dc3545",
  'hold':"#ffc107"
}


const JobCard = ({ header, submitButtonDisabled, submitButtonAction, submitButtonText, skills, handleJobClick, isJobScreening, ...props}) => {
  const jobCardRef = useRef()
  const navigate = useNavigate();
  const [isApplyingLoading, setIsApplyingLoading] = useState(false);
  const [openJobApplicationModal, setOpenJobApplicationModal] = useState(false);

  const appliedJobs = localStorage.getItem(`appliedJobs`) ? JSON.parse(localStorage.getItem(`appliedJobs`)) : [];

  const {user, userAuthenticated} = useSelector((state) => {
    return {
      user: state?.auth?.user,
      userAuthenticated: state?.auth?.accessToken
    }
  });

  useEffect(() => {
    document.body.classList.add('job-page-body');
    
    return () => {
      document.body.classList.remove('job-page-body');
    };
  }, []);

  const handleJobApplyClick = () => {
    setOpenJobApplicationModal(true);
  }

  const handleJobApplicationModalClose = () => {
    setOpenJobApplicationModal(false);
  }

  const handleSubmitJobApplicationData = async (jobApplicationData) => {
    const appliedJobData = localStorage.getItem(`appliedJobData`) ? JSON.parse(localStorage.getItem(`appliedJobData`)) : {};

    if (appliedJobData?.email === jobApplicationData?.candidateEmail && (appliedJobs || [])?.includes(props.uuid)) {
      handleJobApplicationModalClose();
      if (props?.originalJobPageUrl) {
        window.open(props?.originalJobPageUrl, '_blank');
      }
      return;
    }

    setIsApplyingLoading(true);
    const requestBody = {
      jobId: props?.uuid,
      firstName: jobApplicationData?.firstName,
      lastName: jobApplicationData?.lastName,
      email: jobApplicationData?.candidateEmail,
      mobile: {
        phone: jobApplicationData?.phone || "",
        phoneCode: jobApplicationData?.phoneCode || ""
      },
      country: jobApplicationData?.country,
      state: jobApplicationData?.state,
      ...(userAuthenticated ? {candidateId: user?.candidateId} : {})
    };
    let resume = typeof jobApplicationData?.resume === "string" ? jobApplicationData?.resume?.replace(process.env.REACT_APP_PUBLIC_CDN_URL, "") : ""
    if (jobApplicationData?.resume instanceof File) {
      await uploadToS3(jobApplicationData?.resume, "jobApplicationResume", `${props?.uuid}_${dayjs().unix()}`).then((url) => {
        resume = url.replace(process.env.REACT_APP_PUBLIC_CDN_URL, "");
      })
    }

    requestBody.resume = resume;
    await createJobApplication(props?.uuid, requestBody).then(response => {
      setIsApplyingLoading(false);
      if (response?.statusCode >= 200 && response?.statusCode < 400) {
        handleJobApplicationModalClose();
        toast.success(`Your application has been submitted successfully.`);
        localStorage.setItem(`appliedJobData`, JSON.stringify(requestBody));
        appliedJobs.push(props?.uuid)
        localStorage.setItem(`appliedJobs`, JSON.stringify(appliedJobs));
        if (props?.originalJobPageUrl) {
          window.open(props?.originalJobPageUrl, '_blank');
        }
      } else {
        toast.error(response.message || `Error while submitting job application, please try again`);
      }
    }).catch(error => {
      setIsApplyingLoading(false);
      const errorMessage = JSON.parse(error?.response);
      toast.error(errorMessage?.message || `Error while submitting job application, please try again`);
    });
  }

  useLayoutEffect(() => {
    let cardWidth = document.querySelector(`.chip-wrapper-${props.uuid}`).offsetWidth; // or 300
    let tagsWidth = 0;
    let hiddenItems = 0;
    document.querySelectorAll(`.skill-chip-${props.uuid}`).forEach(tag => {
      tagsWidth += tag.offsetWidth;
      if (tagsWidth + 150 > cardWidth) {
        hiddenItems++;
        tag.style.display = 'none';
      }
    })
    if (hiddenItems > 0) {
      document.querySelector(`.chip-flex-wrapper-${props.uuid}`).insertAdjacentHTML('beforeend', `<span class="chip-wrapper">${hiddenItems}+</span>`)
    }
  }, []);

  const isJsonSummary = isJsonString(props?.jobDetails || props?.summary);
  const allSkills = [...(props?.skillsNeeded || []), ...(props?.jobSkills?.primarySkills || []), ...(props?.jobSkills?.secondarySkills || [])];
  const title = props?.title || props?.jobTitle
  const extraGridProps = isJobScreening ? {xs:12, md:12} : {xs:true};

  const isCandidateApplied = !userAuthenticated && (appliedJobs || [])?.includes(props.uuid) ? true : !!props?.isCandidateApplied;

  return (
    <>
      <MainCard mainCardStyle={{backgroundColor: props?.status === "closed" ? "#f6f6f6" : "" }}>
        <div className="wrapper">
          <div className="main-container">
            <div className="searched-jobs">
              <div className="job-cards">
                <div className={`job-card ${props?.status === "closed" ? 'job-card-disabled' : ''}`}>
                  <Box sx={{flexGrow: 1}}>
                    <Grid container spacing={2} className="flex-align-top" ref={(me) => jobCardRef.current = me}>
                      {!isJobScreening ? <Grid item>
                        <LogoWrapper className='cursor-pointer' onClick={() => handleJobClick()}
                                      style={{ backgroundImage: `url(${props?.logo || DEFAULT_COMPANY_LIST_LOGO_URL})` }} />
                      </Grid> : null}
                      <Grid container item spacing={2} {...extraGridProps}>
                        <Grid item xs={12} md={12}>
                          <div className="icon-left" style={{margin:0}} onClick={() => handleJobClick()}>
                            <Title>
                              <FlexRowWrapper>
                                {title?.trim() ? title?.trim() : (props?.role?.trim() || 'No Title')}
                              </FlexRowWrapper>
                              <SubTitle>
                                {props?.businessId?.name}
                              </SubTitle>
                            </Title>
                            <div className="job-card-date">
                              {dayjs(props?.createdAt).fromNow()}
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={8} md={8} >
                          <div className="job-card-subtitle cursor-pointer summary-subtitle"
                                onClick={() => handleJobClick()}>
                            {
                              isJsonSummary ?
                                  <ReadOnly initialValue={JSON.parse(props?.jobDetails || props?.summary) || []}/>
                                  : <MarkDownContent className="slate-editor">{props?.jobDetails || props?.summary}</MarkDownContent>
                            }
                          </div>
                        </Grid>
                        <Grid item xs={4} md={4}>
                          <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            flexDirection: "column",
                            float: "right"
                          }} className="job-card-subtitle">
                            <FlexRowWrapper
                                style={{whiteSpace: "nowrap", flexWrap: "nowrap", justifyContent: "left", gap: 0}}>
                              <HistoryIcon style={{fontWeight: 600}} className="sub-icons"/>&nbsp;
                              <span className="vertical-align-middle">
                              <span
                                  style={{fontWeight: 600}}>Experience:</span>&nbsp;
                                  {
                                    props?.expNeeded?.min === props?.expNeeded?.max ?
                                      `${props?.expNeeded?.min} years`
                                    : `${props?.expNeeded?.min} to ${props?.expNeeded?.max} years`
                                  }
                              </span>
                            </FlexRowWrapper>
                            {props?.timezone !== undefined ?
                              <FlexRowWrapper
                                style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', justifyContent: 'left', gap: 0 }}>
                                <PublicIcon style={{ fontWeight: 600 }} className='sub-icons' />&nbsp;
                                <span className='vertical-align-middle'>
                                  <span style={{ fontWeight: 600 }}>Timezone:</span> {props?.timezone}
                                </span>
                              </FlexRowWrapper>
                              : null
                            }
                            {props?.overlappingNeededHours !== undefined ?
                              <FlexRowWrapper
                                  style={{whiteSpace: "nowrap", flexWrap: "nowrap", justifyContent: "left", gap: 0}}>
                                <AccessTimeIcon style={{fontWeight: 600}} className="sub-icons"/>&nbsp;
                                <span className="vertical-align-middle">
                                <span
                                    style={{fontWeight: 600}}>Overlapping:</span> {props?.overlappingNeededHours} hours
                                </span>
                              </FlexRowWrapper>
                              : null
                            }
                            {isJobScreening  && props?.website ?
                              <FlexRowWrapper
                                style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', justifyContent: 'left', gap: 0 }}>
                                <PublicIcon style={{ fontWeight: 600 }} className='sub-icons' />&nbsp;
                                <span className='vertical-align-middle'>
                                  <span style={{ fontWeight: 600 }}>Website:</span> <a href={props?.website}>Link</a>
                                </span>
                              </FlexRowWrapper>
                              : null}
                            {isJobScreening  && props?.jdUrl ?
                              <FlexRowWrapper
                                style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', justifyContent: 'left', gap: 0 }}>
                                <PublicIcon style={{ fontWeight: 600 }} className='sub-icons' />&nbsp;
                                <span className='vertical-align-middle'>
                                  <span style={{ fontWeight: 600 }}>JD URL:</span> <a href={props?.jdUrl}>Link</a>
                                </span>
                              </FlexRowWrapper>
                              : null}
                            {isJobScreening  && props?.careerPageUrl ?
                              <FlexRowWrapper
                                style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', justifyContent: 'left', gap: 0 }}>
                                <PublicIcon style={{ fontWeight: 600 }} className='sub-icons' />&nbsp;
                                <span className='vertical-align-middle'>
                                  <span style={{ fontWeight: 600 }}>Career Page URL:</span> <a href={props?.careerPageUrl}>Link</a>
                                </span>
                              </FlexRowWrapper>
                              : null}
                          </div>
                        </Grid>
                      </Grid>
                      <Grid item xs={8} md={9} className={`chip-wrapper-${props.uuid}`}>
                        <FlexRowWrapper className={`chip-flex-wrapper-${props.uuid}`}>
                          {
                            Array.isArray(allSkills) && allSkills?.map((skill) => {
                              return (
                                  <SkillChips className={`skill-chip-${props.uuid}`} skillName={skill}/>
                              )
                            })
                          }
                        </FlexRowWrapper>
                      </Grid>
                      <Grid item xs={4} md={3}>
                        <div style={{display: "flex", alignItems: "center", height: "100%", flexDirection:"column"}}>
                            <div className="job-card-buttons">
                              <CustomButton
                                  type={"primary"}
                                  disabled={!!isCandidateApplied || submitButtonDisabled}
                                  onClick={() => {
                                    navigate(`/jobs/${props.uuid}`)
                                    // handleJobApplyClick();
                                  }}
                                  variant="outlined"
                              >
                                {submitButtonText || !!isCandidateApplied ? "Applied" :  "Apply Now"}
                              </CustomButton>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainCard>
      {
        openJobApplicationModal ?
          <JobApplicationModal
            isOpen={openJobApplicationModal}
            handleClose={handleJobApplicationModalClose}
            handleSubmit={handleSubmitJobApplicationData}
            jobId={props?.uuid}
            isApplyingLoading={isApplyingLoading}
          />
        : null
      }
    </>
  )
};

export default withSwal(JobCard)